// =====================================================
// PACKAGE PARTS: relationship ids and content types. Every relationship and content type the
// redline adds to its package (a comments or notes part it needs, the authors list, custom
// properties, a picture or link brought in from the original) is added here, so each .rels part
// gets ids it does not already use.
// =====================================================

import { xmlEscape } from './xml-helpers';

/** The first relationship id of the form `${prefix}N` (rId1, rId2, …) a .rels part does not use. */
export function freeRelId(relsXml: string, prefix = 'rId'): string {
  if (prefix === 'rId') {
    const used = new Set([...relsXml.matchAll(/Id="rId(\d+)"/g)].map(m => parseInt(m[1], 10)));
    let n = 1;
    while (used.has(n)) n++;
    return 'rId' + n;
  }
  let n = 1;
  while (relsXml.includes(`Id="${prefix}${n}"`)) n++;
  return prefix + n;
}

/** A .rels part with one more relationship, under `id` or the first free one of the form
 *  `${prefix}N` (rIdN by default). */
export function withRelationship(relsXml: string, rel: { type: string; target: string; external?: boolean; id?: string; prefix?: string }): { xml: string; id: string } {
  const id = rel.id ?? freeRelId(relsXml, rel.prefix);
  const xml = relsXml.replace('</Relationships>',
    `<Relationship Id="${id}" Type="${rel.type}" Target="${xmlEscape(rel.target)}"${rel.external ? ' TargetMode="External"' : ''}/></Relationships>`);
  return { xml, id };
}

/** Add a relationship to the output's .rels part `relsPath` (when the part exists): see withRelationship. */
export async function addRelationship(zip: any, relsPath: string, rel: { type: string; target: string; id?: string }): Promise<string | null> {
  const rels: string | undefined = await zip.file(relsPath)?.async('string');
  if (!rels) return null;
  const r = withRelationship(rels, rel);
  zip.file(relsPath, r.xml);
  return r.id;
}

/** [Content_Types].xml declaring one more part (an Override), unless it already does. */
export function withOverride(ctXml: string, partName: string, contentType: string): string {
  const pn = partName.startsWith('/') ? partName : '/' + partName;
  if (ctXml.includes(`PartName="${pn}"`)) return ctXml;
  return ctXml.replace('</Types>', `<Override PartName="${pn}" ContentType="${contentType}"/></Types>`);
}

/** Declare an output part's content type (see withOverride), when the output has a content-types part. */
export async function declarePart(zip: any, partName: string, contentType: string): Promise<void> {
  const ct: string | undefined = await zip.file('[Content_Types].xml')?.async('string');
  if (!ct) return;
  const next = withOverride(ct, partName, contentType);
  if (next !== ct) zip.file('[Content_Types].xml', next);
}

export const REL = {
  comments: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments',
  footnotes: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/footnotes',
  endnotes: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/endnotes',
  people: 'http://schemas.microsoft.com/office/2011/relationships/people',
  customProperties: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/custom-properties',
};
export const CONTENT_TYPE = {
  comments: 'application/vnd.openxmlformats-officedocument.wordprocessingml.comments+xml',
  footnotes: 'application/vnd.openxmlformats-officedocument.wordprocessingml.footnotes+xml',
  endnotes: 'application/vnd.openxmlformats-officedocument.wordprocessingml.endnotes+xml',
  people: 'application/vnd.openxmlformats-officedocument.wordprocessingml.people+xml',
  customProperties: 'application/vnd.openxmlformats-officedocument.custom-properties+xml',
};

